import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Phone, Mail, MapPin, Calculator, Linkedin } from "lucide-react";
import ClientQuote from "./ClientQuote";
import api from "../../lib/api";
import { FIRM, HERO, SERVICES, WHY, SECTORS, IMAGES, PUBLIC_TOOLS, STANDARDS } from "./siteData";
import { GalleryPreview } from "./Gallery";
import { Backdrop } from "./PageHero";
import { useSeo } from "./useSeo";
import { useEnquiry } from "./enquiryContext";
import EnquiryForm from "./EnquiryForm";

const ORG_LD = {
  "@context": "https://schema.org", "@type": "AccountingService", name: FIRM.name,
  url: "https://vcaj.in", telephone: FIRM.phone, email: FIRM.email, sameAs: [FIRM.linkedin],
  address: { "@type": "PostalAddress", streetAddress: FIRM.address.slice(0, 2).join(" "), addressLocality: "Chennai", postalCode: "600006", addressCountry: "IN" },
};

function Hero() {
  const openEnquiry = useEnquiry();
  return (
    <section className="relative isolate overflow-hidden bg-[#14263D] text-white" data-testid="home-hero">
      <Backdrop image={IMAGES.home} />
      <div className="absolute inset-0 -z-10 site-lines opacity-50" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16 lg:pt-28 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7 site-reveal">
          <div className="site-eyebrow">{HERO.eyebrow || `Chartered Accountants · Chennai · Since ${FIRM.founded}`}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.08] mt-6" data-testid="home-hero-title">
            {HERO.lines.filter(Boolean).map((l, i, a) => (
              <span key={l} className={`block ${i === a.length - 1 && a.length > 1 ? "text-[#C4A66A] italic font-medium" : ""}`}>{l}</span>
            ))}
          </h1>
          <p className="mt-8 text-base md:text-lg text-white/75 max-w-xl leading-relaxed" data-testid="home-hero-tagline">
            {HERO.intro}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button onClick={() => openEnquiry()} className="btn-site-gold h-12 px-7 text-sm font-semibold inline-flex items-center gap-2" data-testid="hero-enquire-btn">Discuss a mandate <ArrowUpRight className="h-4 w-4" /></button>
            <a href="#expertise" className="h-12 px-7 text-sm font-semibold inline-flex items-center gap-2 border border-white/25 hover:border-[#C4A66A] hover:text-[#C4A66A] transition-colors" data-testid="hero-explore-btn">Explore verticals</a>
          </div>
        </div>
        <div className="lg:col-span-5 site-reveal" data-testid="hero-verticals">
          <div className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-4">Our verticals</div>
          {SERVICES.map((s) => (
            <Link key={s.slug} to={`/expertise/${s.slug}`} className="group flex items-center gap-5 py-4 border-t border-white/10 last:border-b hover:pl-2 transition-[padding] duration-300" data-testid={`hero-index-${s.slug}`}>
              <span className="font-serif text-[#C4A66A] text-lg w-7">{s.no}</span>
              <span className="font-serif text-xl lg:text-2xl flex-1 group-hover:text-[#C4A66A] transition-colors">{s.title}</span>
              <ArrowRight className="h-4 w-4 text-white/30 group-hover:text-[#C4A66A] group-hover:translate-x-1 transition-transform" />
            </Link>
          ))}
        </div>
      </div>
      <ClientQuote />
    </section>
  );
}

function ExpertiseGrid() {
  return (
    <section id="expertise" className="py-24 lg:py-32 bg-white scroll-mt-20" data-testid="home-expertise">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <div className="site-eyebrow">Verticals</div>
            <h2 className="font-serif text-3xl lg:text-5xl font-semibold text-[#14263D] mt-4">Our verticals. One accountable team.</h2>
          </div>
          <p className="lg:col-span-5 text-slate-600 leading-relaxed">Each vertical is led by a partner and draws on the others when a mandate needs it — so a diligence finding becomes a tax position, and a tax position becomes a reporting answer.</p>
        </div>
        <div className="mt-16 grid md:grid-cols-6 gap-5">
          {SERVICES.map((s, i) => (
            <Link key={s.slug} to={`/expertise/${s.slug}`}
              className={`site-card-lift group relative overflow-hidden border border-slate-200 p-8 lg:p-10 flex flex-col ${i < 2 ? "md:col-span-3 min-h-[340px] bg-[#14263D] text-white border-[#14263D]" : "md:col-span-2 min-h-[300px] bg-white"}`}
              data-testid={`expertise-card-${s.slug}`}>
              <span className={`site-outline-num absolute -right-2 -bottom-6 text-[150px] ${i < 2 ? "" : "opacity-60"}`}>{s.no}</span>
              <s.icon className="h-7 w-7 text-[#C4A66A]" strokeWidth={1.5} />
              <h3 className={`font-serif text-2xl lg:text-3xl font-semibold mt-8 ${i < 2 ? "" : "text-[#14263D]"}`}>{s.title}</h3>
              <p className={`mt-4 text-sm leading-relaxed max-w-md ${i < 2 ? "text-white/70" : "text-slate-600"}`}>{s.short}</p>
              <span className={`mt-auto pt-8 text-[11px] uppercase tracking-[0.25em] inline-flex items-center gap-2 ${i < 2 ? "text-[#C4A66A]" : "text-[#14263D]"}`}>
                Explore vertical <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyAndSectors() {
  return (
    <section className="py-24 lg:py-32 bg-[#F7F4EC]" data-testid="home-why">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <div className="site-eyebrow">Why VCAJ</div>
          <h2 className="font-serif text-3xl lg:text-5xl font-semibold text-[#14263D] mt-4">How we earn trust.</h2>
          <div className="site-rule mt-8" />
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-12 gap-y-12">
          {WHY.map((w, i) => (
            <div key={w.t} data-testid={`why-item-${i}`}>
              <div className="font-serif text-[#C4A66A] text-xl">0{i + 1}</div>
              <h3 className="font-serif text-2xl font-semibold text-[#14263D] mt-2">{w.t}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed text-sm">{w.d}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-24" data-testid="home-sectors">
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-6">Sectors we serve</div>
        <div className="flex flex-wrap gap-3">
          {SECTORS.map((s) => (
            <span key={s} className="px-5 py-3 bg-white border border-[#C4A66A]/30 text-sm text-[#14263D] hover:border-[#C4A66A] transition-colors flex items-center gap-3">
              <span className="h-1.5 w-1.5 bg-[#C4A66A]" />{s}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function PublicationsTeaser() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/public/site/publications").then((r) => setItems(r.data.slice(0, 3))).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <section className="py-24 lg:py-32 bg-white" data-testid="home-publications">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="site-eyebrow">Let's Learn</div>
            <h2 className="font-serif text-3xl lg:text-5xl font-semibold text-[#14263D] mt-4">Insights.</h2>
          </div>
          <Link to="/lets-learn" className="text-xs uppercase tracking-[0.25em] text-[#14263D] site-link inline-flex items-center gap-2" data-testid="home-publications-all">All articles <ArrowRight className="h-3.5 w-3.5" /></Link>
        </div>
        <div className="mt-14 grid md:grid-cols-3 border-t border-slate-200">
          {items.map((p) => (
            <Link key={p.id} to={`/lets-learn?read=${p.id}`} className="group py-8 md:pr-8 md:border-r last:border-r-0 border-b md:border-b-0 border-slate-200 md:[&:not(:first-child)]:pl-8" data-testid={`home-pub-${p.id}`}>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#C4A66A] flex items-center gap-2">{p.source === "linkedin" && <Linkedin className="h-3.5 w-3.5" />}{p.source === "linkedin" ? "LinkedIn post" : p.category}</div>
              <h3 className="font-serif text-2xl font-semibold text-[#14263D] mt-4 leading-snug group-hover:underline decoration-[#C4A66A] underline-offset-4">{p.title}</h3>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{p.summary}</p>
              <div className="text-xs text-slate-400 mt-5">{p.published_on}{p.source === "linkedin" ? "" : ` · ${p.read_minutes} min read`}</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function StandardsBand() {
  return (
    <section className="bg-[#0C1726] text-white py-10 overflow-hidden border-y border-[#C4A66A]/20" data-testid="home-standards">
      <div className="flex whitespace-nowrap site-marquee w-max hover:[animation-play-state:paused]">
        {[...STANDARDS, ...STANDARDS].map(([t, href], i) => (
          <a key={i} href={href} target="_blank" rel="noopener noreferrer" title={`Open ${t} (official source)`}
            className="font-serif text-2xl lg:text-3xl px-8 text-white/80 hover:text-[#C4A66A] transition-colors flex items-center gap-8"
            data-testid={i < STANDARDS.length ? `standard-link-${i}` : undefined}>
            <span className="border-b border-transparent hover:border-[#C4A66A]">{t}</span><span className="h-1.5 w-1.5 bg-[#C4A66A] rotate-45" />
          </a>
        ))}
      </div>
    </section>
  );
}

function ToolsSection() {
  return (
    <section className="relative isolate overflow-hidden py-24 lg:py-32 bg-[#14263D] text-white" data-testid="home-tools">
      <Backdrop image={IMAGES.tools} />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <div className="site-eyebrow">Tools</div>
            <h2 className="font-serif text-3xl lg:text-5xl font-semibold mt-4">Free technical calculators.</h2>
          </div>
          <p className="lg:col-span-5 text-white/70 leading-relaxed">Built and used by our engagement teams, now open to you. No sign-up needed: results are computed instantly and the figures you enter are not saved.</p>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
          {PUBLIC_TOOLS.map((t) => (
            <Link key={t.id} to={`/tools/${t.id}`} className="group bg-[#14263D]/80 backdrop-blur-md p-7 flex flex-col min-h-[240px] hover:bg-[#1E3654] transition-colors duration-300" data-testid={`home-tool-${t.id}`}>
              <Calculator className="h-6 w-6 text-[#C4A66A]" strokeWidth={1.5} />
              <div className="mt-6 text-[10px] uppercase tracking-[0.25em] text-[#C4A66A]">{t.tag}</div>
              <h3 className="font-serif text-xl font-semibold mt-2 leading-snug">{t.name}</h3>
              <span className="mt-auto pt-6 text-[11px] uppercase tracking-[0.25em] text-white/70 group-hover:text-[#C4A66A] inline-flex items-center gap-2">Use tool <ArrowUpRight className="h-3.5 w-3.5" /></span>
            </Link>
          ))}
        </div>
        <Link to="/tools" className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#C4A66A] site-link" data-testid="home-tools-all">All tools <ArrowRight className="h-3.5 w-3.5" /></Link>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section id="contact" className="relative isolate overflow-hidden py-24 lg:py-32 bg-[#14263D] text-white scroll-mt-20" data-testid="home-contact">
      <Backdrop image={IMAGES.contact} />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="site-eyebrow">Contact</div>
          <h2 className="font-serif text-3xl lg:text-5xl font-semibold mt-4">Start a conversation.</h2>
          <p className="text-white/65 mt-6 leading-relaxed max-w-md">Share a few lines about your requirement. A partner will review it and respond within one business day.</p>
          <ul className="mt-10 space-y-5 text-sm">
            <li className="flex gap-4"><Phone className="h-5 w-5 text-[#C4A66A]" /><a href={FIRM.phoneHref} className="site-link" data-testid="contact-phone">{FIRM.phone}</a></li>
            <li className="flex gap-4"><Mail className="h-5 w-5 text-[#C4A66A]" /><a href={`mailto:${FIRM.email}`} className="site-link" data-testid="contact-email">{FIRM.email}</a></li>
            <li className="flex gap-4"><Linkedin className="h-5 w-5 text-[#C4A66A]" /><a href={FIRM.linkedin} target="_blank" rel="noopener noreferrer" className="site-link" data-testid="contact-linkedin">Follow us on LinkedIn</a></li>
            <li className="flex gap-4"><MapPin className="h-5 w-5 text-[#C4A66A] shrink-0" /><span className="text-white/75">{FIRM.address.map((l) => <span key={l} className="block">{l}</span>)}</span></li>
          </ul>
        </div>
        <div className="lg:col-span-7 text-slate-900"><EnquiryForm /></div>
      </div>
    </section>
  );
}

export default function Home() {
  useSeo("Adding Value. Building Trust. Leading Excellence.",
    "VCAJ & Associates LLP is a Chartered Accountancy firm offering Audit & Risk Assurance, Tax & Regulatory, Transactions, Accounting & Reporting and Virtual CFO services.", ORG_LD);
  return (
    <div data-testid="home-page">
      <Hero />
      <ExpertiseGrid />
      <WhyAndSectors />
      <StandardsBand />
      <PublicationsTeaser />
      <ToolsSection />
      <GalleryPreview />
      <Contact />
    </div>
  );
}
